<template>
    <div class="relative inline-block text-left">
        <AppButton element="button" @click="open = !open" type="button">
            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                    fill-rule="evenodd"
                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                    clip-rule="evenodd"
                ></path>
            </svg>
            {{ $t('messages.create') }}
            <SlideOver size="xxl" :show="open" @close="open = false">
                <form :action="url" method="post" @submit.prevent="submit">
                    <div class="md:grid md:grid-cols-12 md:gap-6">
                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.title')" for="title" />
                                <FormInput
                                    required
                                    id="title"
                                    v-model="form.title"
                                    name="title"
                                    type="text"
                                    max="191"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.title" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.description')" for="description" />
                                <FormTextarea
                                    id="description"
                                    v-model="form.description"
                                    name="description"
                                    class="mt-1"
                                />
                                <FormError :value="form.errors.description" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.stages', 1)" for="stage_id" />
                                <FormSelect
                                    id="stage_id"
                                    v-model="form.stage_id"
                                    :options="options.stages"
                                    class="mt-1"
                                />
                                <FormError :value="form.errors.stage_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                            <div v-if="filteredGradeLevels.length === 0" class="text-gray-600">
                                {{ $t('messages.select') }}: {{ $tc('messages.stages', 1) }}
                            </div>
                            <div class="mt-1">
                                <label v-for="grade_level in filteredGradeLevels" class="mr-1" :key="grade_level.value">
                                    <input
                                        v-model="form.grade_level_ids"
                                        name="grade_level_ids"
                                        :value="grade_level.value"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    {{ grade_level.text }}
                                </label>
                            </div>
                            <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                        </div>
                        <div class="sm:col-span-12">
                            <FormLabel :value="$t('messages.dimensions')" for="dimensions" />
                            <FormError :value="form.errors.dimension_ids" class="mt-2" />
                            <CategoryDimensionsSelectorSummary
                                :categories="filteredCategories"
                                :model-value="form.dimension_ids"
                                @remove-dimension="removeDimension"
                            />
                            <div v-if="filteredCategories.length === 0" class="text-gray-600">
                                {{ $t('messages.select') }}: {{ $tc('messages.stages', 1) }}
                            </div>
                            <CategoryDimensionsSelector
                                :categories="filteredCategories"
                                v-else
                                v-model="form.dimension_ids"
                                :default-category="defaults.category_id"
                            />
                        </div>
                    </div>

                    <div class="flex justify-end space-x-2">
                        <SubmitFormButton
                            @click="another = true"
                            :button-text="$t('messages.save_and_another')"
                            :processing="form.processing"
                        />
                        <SubmitFormButton
                            @click="another = false"
                            :button-text="$t('messages.save')"
                            :processing="form.processing"
                        />
                    </div>
                </form>
            </SlideOver>
        </AppButton>
    </div>
</template>

<script>
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import SlideOver from '@/components/SlideOver.vue';
import AppButton from '@/components/AppButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import CategoryDimensionsSelector from '@/components/CategoryDimensionsSelector.vue';
import CategoryDimensionsSelectorSummary from '@/components/CategoryDimensionsSelectorSummary.vue';

export default {
    emits: ['close'],
    name: 'EvaluationCriteriaCreateForm',

    components: {
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        SlideOver,
        AppButton,
        SubmitFormButton,
        FormTextarea,
        CategoryDimensionsSelectorSummary,
        CategoryDimensionsSelector,
    },

    props: {
        options: {
            required: true,
            type: Object,
        },
        url: {
            required: true,
            type: String,
        },
        defaults: {
            required: false,
            type: Object,
        },
    },
    data() {
        return {
            open: false,
            another: false,
            form: this.$inertia.form({
                title: '',
                description: '',
                stage_id: this.defaults.stage_id,
                dimension_ids: [],
                grade_level_ids: this.defaults.grade_level_ids,
                redirect_back: true,
            }),
        };
    },
    computed: {
        filteredGradeLevels() {
            if (!this.form.stage_id) {
                return [];
            }
            return this.options.gradeLevels.filter((s) => s.stage_id == this.form.stage_id);
        },
        filteredCategories() {
            if (!this.form.stage_id) {
                return [];
            }
            return this.options.categories.filter((c) => c.stage_id == this.form.stage_id);
        },
    },
    watch: {
        'form.stage_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.form.grade_level_ids = [];
                    this.form.dimension_ids = [];
                }
            },
            deep: true,
        },
    },
    methods: {
        dismiss() {
            this.$emit('close');
        },
        submit() {
            this.form.post(this.url, {
                onSuccess: () => {
                    if (this.another) {
                        this.form.reset('title', 'description');
                    } else {
                        this.form.reset('title', 'description', 'dimension_ids');
                        this.open = false;
                    }

                    this.$emit('include-new-content');
                },
                preserveScroll: true,
            });
        },
        removeDimension(id) {
            const index = this.form.dimension_ids.indexOf(id);
            if (index !== -1) {
                this.form.dimension_ids.splice(index, 1);
            }
        },
    },
};
</script>
